<script setup>
  import { computed, onMounted, ref, watch } from "vue";
  import { Loader2, FileCheck, ChevronDown, CircleCheck } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";
  import { storeToRefs } from "pinia";
  import { useRoute } from "vue-router";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";
  import { useSurveyErrorsStore } from "@stores/survey/surveyErrors";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
  import { useQuotasStore } from "@stores/survey/quotas";
  import { useTaskProgressesStore } from "@stores/taskProgresses";
  import { useProfileListStore } from "@stores/survey/profiles/profileList";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useCardsStore } from "@stores/survey/cards";
  import { useSectionsStore } from "@stores/survey/sections";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";

  const route = useRoute();

  const props = defineProps({
    survey: { type: Object, required: true },
    isReadOnly: { type: Boolean, required: true },
  });

  const surveyProfilesStore = useSurveyProfilesStore();
  const surveyErrorsStore = useSurveyErrorsStore();
  const surveyTargetsStore = useSurveyTargetsStore();
  const quotasStore = useQuotasStore();
  const surveyStore = useSurveyStore();
  const { lastProgressUpdate, taskProgresses } = storeToRefs(useTaskProgressesStore());
  const surveyCardsStore = useSurveyCardsStore();
  const cardsStore = useCardsStore();
  const sectionsStore = useSectionsStore();
  const currentCardStore = useCurrentCardStore();
  const profileListStore = useProfileListStore();

  const isSaveLoading = ref(false);
  const isSaveAsDraftLoading = ref(false);
  const isSaveButtonLastClicked = ref(true);
  const isTaskInProgress = ref(false);
  const surveyTaskTypes = ["create_survey_ai_powered", "create_survey_word", "translate_survey"];

  const { hasSurveyChanged } = storeToRefs(surveyStore);
  const { hasProfilesChanged, hasProfilesErrors } = storeToRefs(surveyProfilesStore);
  const { hasErrors } = storeToRefs(surveyErrorsStore);
  const { hasTargetsChanged, hasTargetsErrors } = storeToRefs(surveyTargetsStore);
  const { hasChanged: hasQuotasChanged, hasErrors: hasQuotasErrors } = storeToRefs(quotasStore);
  const { hasUnsavedChanges: hasCardsChanged } = storeToRefs(cardsStore);
  const { hasUnsavedChanges: hasSectionsChanged } = storeToRefs(sectionsStore);
  const { cards } = storeToRefs(surveyCardsStore);
  const { selectFirstCardAsCurrent, updateCardsBeforeSave } = surveyCardsStore;
  const { currentIndex, currentCard } = storeToRefs(currentCardStore);
  const { selectCard } = currentCardStore;

  const hasGlobalChanges = computed(
    () =>
      hasSurveyChanged.value ||
      hasProfilesChanged.value ||
      hasTargetsChanged.value ||
      hasQuotasChanged.value ||
      hasCardsChanged.value ||
      hasSectionsChanged.value,
  );
  const hasGlobalErrors = computed(
    () => hasErrors.value || hasProfilesErrors.value || hasTargetsErrors.value || hasQuotasErrors.value,
  );
  const buttonVariant = computed(() => {
    if (hasGlobalErrors.value && isSaveButtonLastClicked.value) return "destructive";
    return "default";
  });
  const isSaveDisabled = computed(
    () =>
      isSaveLoading.value ||
      (isSaveButtonLastClicked.value && !hasGlobalChanges.value && !hasGlobalErrors.value) ||
      isTaskInProgress.value,
  );
  const isSaveDraftDisabled = computed(
    () =>
      isSaveAsDraftLoading.value ||
      (isSaveButtonLastClicked.value && !hasGlobalChanges.value && !hasGlobalErrors.value) ||
      (!isSaveButtonLastClicked.value && !hasGlobalChanges.value) ||
      isTaskInProgress.value,
  );

  const isClean = computed(
    () =>
      !hasGlobalChanges.value &&
      !hasGlobalErrors.value &&
      !isSaveLoading.value &&
      !isSaveAsDraftLoading.value &&
      !isTaskInProgress.value,
  );

  // Split button: show dropdown only in draft mode
  const canSaveAsDraft = computed(() => props.survey.status === "draft" && route.name !== "SurveyProfiles");

  const handleSave = async (skipValidation = false, isSaveButton = true, saveAsDraft = false) => {
    if (isSaveButton) isSaveLoading.value = true;
    else isSaveAsDraftLoading.value = true;

    try {
      await globalSaveSurvey(skipValidation, saveAsDraft);
    } finally {
      if (isSaveButton) isSaveLoading.value = false;
      else isSaveAsDraftLoading.value = false;
      isSaveButtonLastClicked.value = isSaveButton; // checking which button was clicked last
    }
  };

  onMounted(() => {
    taskProgresses.value.map((task) => {
      if (
        !["Complete", "Failed"].includes(task.status) &&
        surveyTaskTypes.includes(task.taskInfo?.type) &&
        task.taskInfo?.surveyToken === props.survey.token
      ) {
        isTaskInProgress.value = true;
      }
    });
  });

  watch(lastProgressUpdate, (update) => {
    const isSurveyTask =
      surveyTaskTypes.includes(update.taskInfo?.type) && update.taskInfo?.surveyToken === props.survey.token;

    if (isSurveyTask) {
      isTaskInProgress.value = !["Complete", "Failed"].includes(update.status);
    }
  });
  async function globalSaveSurvey(skipValidation, saveAsDraft) {
    if (hasProfilesChanged.value || hasProfilesErrors.value) {
      await surveyProfilesStore.save();
      profileListStore.selectProfile(0);
    }
    //targets
    if (hasTargetsChanged.value || hasTargetsErrors.value) {
      await surveyTargetsStore.save();
    }
    await surveyErrorsStore.cleanErrors();
    updateCardsBeforeSave();
    const deltaErrors = [...(await sectionsStore.saveAll()), ...(await cardsStore.saveAll())];
    await surveyStore.save({ skipValidation, saveAsDraft });
    if (deltaErrors.length) {
      const errors = deltaErrors.flatMap(({ token, messages }) => messages.map((message) => ({ path: token, message })));
      surveyStore.survey.errors = [...(surveyStore.survey.errors ?? []), ...errors];
    }

    if (hasQuotasChanged.value || hasQuotasErrors.value) await quotasStore.save();

    if (!hasCardsChanged.value && !hasSectionsChanged.value) {
      await Promise.all([cardsStore.fetch(), sectionsStore.fetch()]);
    }

    if (currentIndex.value && currentIndex.value >= 0) {
      const cardToSelect = cards.value.find((card) => card.token === currentCard.value.token);
      const index = cards.value.findIndex((card) => card.token === currentCard.value.token);
      await selectCard({ card: cardToSelect, index: index });
    } else await selectFirstCardAsCurrent();
  }
</script>

<template>
  <div
    v-if="!isReadOnly"
    class="flex">
    <span
      v-if="isClean"
      class="inline-flex h-8 items-center gap-1.5 px-2 text-13 text-muted-foreground">
      <CircleCheck class="size-4 text-status-success" />
      Saved
    </span>
    <!-- Simple Save button when not in draft mode -->
    <Button
      v-else-if="!canSaveAsDraft"
      :variant="buttonVariant"
      :disabled="isSaveDisabled"
      @click="handleSave()">
      <Loader2
        v-if="isSaveLoading"
        class="h-4 w-4 mr-2 animate-spin" />
      Save
    </Button>

    <!-- Split button when in draft mode -->
    <template v-else>
      <!-- Primary button: Save as draft when errors, Save otherwise -->
      <Button
        :variant="buttonVariant"
        :disabled="hasGlobalErrors ? isSaveDraftDisabled : isSaveDisabled"
        class="rounded-r-none"
        @click="hasGlobalErrors ? handleSave(true, false, true) : handleSave()">
        <Loader2
          v-if="hasGlobalErrors ? isSaveAsDraftLoading : isSaveLoading"
          class="h-4 w-4 mr-2 animate-spin" />
        <FileCheck
          v-else-if="hasGlobalErrors"
          class="h-4 w-4 mr-2" />
        {{ hasGlobalErrors ? "Save as draft" : "Save" }}
      </Button>

      <!-- Dropdown with secondary action -->
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <Button
            :variant="buttonVariant"
            :disabled="hasGlobalErrors ? isSaveDraftDisabled : isSaveDisabled"
            class="rounded-l-none border-l border-l-primary-foreground/20 px-2">
            <ChevronDown class="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            :disabled="hasGlobalErrors ? isSaveDisabled : isSaveDraftDisabled"
            @click="hasGlobalErrors ? handleSave() : handleSave(true, false, true)">
            <FileCheck
              v-if="!hasGlobalErrors"
              class="h-4 w-4 mr-2" />
            {{ hasGlobalErrors ? "Save" : "Save as draft" }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </template>
  </div>
</template>
